import { supabase } from '../config/supabase';
import logger from './logger';

const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL || '';

export type AttestationType = 'identity' | 'property' | 'questionnaire' | 'agreement';
export type AttestationStatus = 'pending' | 'verified' | 'rejected' | 'revoked';

export interface AttestationRecord {
  id?: string;
  attestation_uid: string;
  wallet_address: string;
  attestation_type: AttestationType;
  status: AttestationStatus;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  verified_by?: string;
  verified_at?: string;
  notes?: string;
}

interface AdminMutationOptions {
  walletClient?: any;
}

interface AdminStatusMutationRequest {
  attestationUID: string;
  status: AttestationStatus;
  actorWallet: string;
  notes?: string;
  authMessage: string;
  signature: string;
}

export class AttestationTracker {
  private static async performLocalStatusUpdate(
    attestationUID: string,
    status: AttestationStatus,
    verifiedBy: string,
    notes?: string
  ): Promise<boolean> {
    const updateData: Partial<AttestationRecord> = {
      status,
      verified_by: verifiedBy.toLowerCase(),
      verified_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    if (notes) {
      updateData.notes = notes;
    }

    const { error } = await supabase
      .from('attestations')
      .update(updateData)
      .eq('attestation_uid', attestationUID);

    if (error) {
      console.error(`❌ Error updating attestation to ${status}:`, error);
      return false;
    }

    return true;
  }

  private static async buildAdminSignaturePayload(
    attestationUID: string,
    status: AttestationStatus,
    actorWallet: string,
    walletClient: any
  ): Promise<Pick<AdminStatusMutationRequest, 'authMessage' | 'signature'>> {
    const normalizedActorWallet = actorWallet.toLowerCase();
    const authMessage = [
      'Authorize Attestation Status Update',
      '',
      `Attestation UID: ${attestationUID}`,
      `Status: ${status}`,
      `Actor: ${normalizedActorWallet}`,
      `Timestamp: ${Date.now()}`,
    ].join('\n');

    const signature = await walletClient.signMessage({
      account: actorWallet as `0x${string}`,
      message: authMessage,
    });

    return { authMessage, signature };
  }

  private static async performAdminStatusUpdate(
    attestationUID: string,
    status: AttestationStatus,
    actorWallet: string,
    notes: string | undefined,
    walletClient: any
  ): Promise<boolean> {
    const { authMessage, signature } = await this.buildAdminSignaturePayload(
      attestationUID,
      status,
      actorWallet,
      walletClient
    );

    const response = await fetch(`${ADMIN_API_URL}/attestations/update-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attestationUID,
        status,
        actorWallet,
        notes,
        authMessage,
        signature,
      } satisfies AdminStatusMutationRequest),
    });

    let payload: { success?: boolean; error?: string } | null = null;
    try {
      payload = await response.json() as { success?: boolean; error?: string };
    } catch {
      payload = null;
    }

    if (!response.ok || !payload?.success) {
      throw new Error(payload?.error || `Admin status service returned ${response.status}`);
    }

    return true;
  }

  /**
   * Record a new attestation when it's created
   */
  static async recordAttestation(
    attestationUID: string,
    walletAddress: string,
    type: AttestationType,
    createdBy?: string
  ): Promise<AttestationRecord | null> {
    try {
      logger.log(`📝 Recording ${type} attestation:`, attestationUID);

      const record: AttestationRecord = {
        attestation_uid: attestationUID,
        wallet_address: walletAddress.toLowerCase(),
        attestation_type: type,
        status: 'pending',
        created_by: createdBy?.toLowerCase()
      };

      const { data, error } = await supabase
        .from('attestations')
        .insert([record])
        .select()
        .single();

      if (error) {
        console.error('❌ Error recording attestation:', error);
        return null;
      }

      logger.log('✅ Attestation recorded successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ Error recording attestation:', error);
      return null;
    }
  }

  /**
   * Update attestation verification status (admin use)
   */
  static async updateVerificationStatus(
    attestationUID: string,
    status: AttestationStatus,
    verifiedBy: string,
    notes?: string,
    options?: AdminMutationOptions
  ): Promise<boolean> {
    try {
      logger.log(`🔄 Updating attestation ${attestationUID} to ${status}`);

      if (ADMIN_API_URL && options?.walletClient) {
        try {
          return await this.performAdminStatusUpdate(
            attestationUID,
            status,
            verifiedBy,
            notes,
            options.walletClient
          );
        } catch (error) {
          logger.warn('Admin status service failed, falling back to local compatibility path:', error);
        }
      }

      const success = await this.performLocalStatusUpdate(attestationUID, status, verifiedBy, notes);
      if (!success) {
        return false;
      }

      logger.log(`✅ Attestation ${attestationUID} updated to ${status}`);
      return true;
    } catch (error) {
      console.error('❌ Error updating attestation status:', error);
      return false;
    }
  }

  /**
   * Get all attestations for a wallet address
   */
  static async getAttestationsForWallet(walletAddress: string): Promise<AttestationRecord[]> {
    try {
      logger.log('🔍 Getting attestations for wallet:', walletAddress);

      const { data, error } = await supabase
        .from('attestations')
        .select('*')
        .eq('wallet_address', walletAddress.toLowerCase())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error getting attestations:', error);
        return [];
      }

      logger.log(`✅ Found ${data?.length || 0} attestations for wallet`);
      return data || [];
    } catch (error) {
      console.error('❌ Error getting attestations:', error);
      return [];
    }
  }

  /**
   * Get attestations by type for a wallet
   */
  static async getAttestationByType(
    walletAddress: string,
    type: AttestationType
  ): Promise<AttestationRecord | null> {
    try {
      const { data, error } = await supabase
        .from('attestations')
        .select('*')
        .eq('wallet_address', walletAddress.toLowerCase())
        .eq('attestation_type', type)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        // No records found is not an error for our use case
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error(`❌ Error getting ${type} attestation:`, error);
        return null;
      }

      return data;
    } catch (error) {
      console.error(`❌ Error getting ${type} attestation:`, error);
      return null;
    }
  }

  /**
   * Get latest active (non-revoked) attestation UID for a wallet and type
   */
  static async getLatestActiveUIDByType(
    walletAddress: string,
    type: AttestationType
  ): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('attestations')
        .select('attestation_uid')
        .eq('wallet_address', walletAddress.toLowerCase())
        .eq('attestation_type', type)
        .neq('status', 'revoked')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error(`❌ Error getting latest active ${type} UID:`, error);
        return null;
      }

      return data?.attestation_uid || null;
    } catch (error) {
      console.error(`❌ Error getting latest active ${type} UID:`, error);
      return null;
    }
  }

  /**
   * Get all pending attestations (admin use)
   */
  static async getPendingAttestations(): Promise<AttestationRecord[]> {
    try {
      const { data, error } = await supabase
        .from('attestations')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error getting pending attestations:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('❌ Error getting pending attestations:', error);
      return [];
    }
  }

  /**
   * Mark attestation as revoked (when revoked on blockchain)
   */
  static async markAsRevoked(
    attestationUID: string,
    revokedBy: string,
    notes?: string,
    options?: AdminMutationOptions
  ): Promise<boolean> {
    try {
      logger.log(`🚫 Marking attestation ${attestationUID} as revoked`);

      const success = await this.updateVerificationStatus(
        attestationUID,
        'revoked',
        revokedBy,
        notes,
        options
      );

      if (!success) {
        return false;
      }

      logger.log(`✅ Attestation ${attestationUID} marked as revoked`);
      return true;
    } catch (error) {
      console.error('❌ Error marking attestation as revoked:', error);
      return false;
    }
  }

  /**
   * Get active (non-revoked) attestations for a wallet address
   */
  static async getActiveAttestationsForWallet(walletAddress: string): Promise<AttestationRecord[]> {
    try {
      logger.log('🔍 Getting active attestations for wallet:', walletAddress);

      const { data, error } = await supabase
        .from('attestations')
        .select('*')
        .eq('wallet_address', walletAddress.toLowerCase())
        .neq('status', 'revoked') // Exclude revoked attestations
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error getting active attestations:', error);
        return [];
      }

      logger.log(`✅ Found ${data?.length || 0} active attestations for wallet`);
      return data || [];
    } catch (error) {
      console.error('❌ Error getting active attestations:', error);
      return [];
    }
  }

  /**
   * Check if wallet has an active (non-revoked) attestation of a specific type
   */
  static async hasActiveAttestation(
    walletAddress: string,
    type: AttestationType
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('attestations')
        .select('id')
        .eq('wallet_address', walletAddress.toLowerCase())
        .eq('attestation_type', type)
        .neq('status', 'revoked') // Exclude revoked
        .limit(1);

      if (error) {
        console.error(`❌ Error checking active ${type} attestation:`, error);
        return false;
      }

      return (data?.length || 0) > 0;
    } catch (error) {
      console.error(`❌ Error checking active ${type} attestation:`, error);
      return false;
    }
  }

  /**
   * Check if wallet has a verified attestation of a specific type
   */
  static async hasVerifiedAttestation(
    walletAddress: string,
    type: AttestationType
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('attestations')
        .select('id')
        .eq('wallet_address', walletAddress.toLowerCase())
        .eq('attestation_type', type)
        .eq('status', 'verified')
        .limit(1);

      if (error) {
        console.error(`❌ Error checking verified ${type} attestation:`, error);
        return false;
      }

      return (data?.length || 0) > 0;
    } catch (error) {
      console.error(`❌ Error checking verified ${type} attestation:`, error);
      return false;
    }
  }

  /**
   * Get attestation by UID
   */
  static async getAttestationByUID(attestationUID: string): Promise<AttestationRecord | null> {
    try {
      logger.log(`🔍 Getting attestation by UID: ${attestationUID}`);
      
      const { data, error } = await supabase
        .from('attestations')
        .select('*')
        .eq('attestation_uid', attestationUID)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows found
          logger.log(`📋 No attestation found for UID: ${attestationUID}`);
          return null;
        }
        console.error('❌ Error getting attestation by UID:', error);
        return null;
      }

      logger.log(`✅ Found attestation: ${data.attestation_type} (${data.status})`);
      return data;
    } catch (error) {
      console.error('❌ Error getting attestation by UID:', error);
      return null;
    }
  }

  /**
   * Get all attestations (pending, verified, rejected, revoked)
   */
  static async getAllAttestations(): Promise<AttestationRecord[]> {
    try {
      logger.log('📊 Getting all attestations from database...');
      
      const { data, error } = await supabase
        .from('attestations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error getting all attestations:', error);
        return [];
      }

      logger.log(`✅ Found ${data?.length || 0} total attestations`);
      return data || [];
    } catch (error) {
      console.error('❌ Error getting all attestations:', error);
      return [];
    }
  }

  /**
   * Check blockchain for revoked attestations and update database
   */
  static async syncRevokedStatus(attestationUIDs: string[]): Promise<void> {
    try {
      logger.log('🔄 Syncing revocation status from blockchain...');
      
      // This would need to be implemented with actual blockchain queries
      // For now, this is a placeholder for the blockchain sync logic
      
      // Example implementation:
      // for (const uid of attestationUIDs) {
      //   const attestation = await eas.getAttestation(uid);
      //   if (attestation.revoked) {
      //     await this.markAsRevoked(uid, attestation.revokedBy || 'system');
      //   }
      // }
      
      logger.log('⚠️ Blockchain revocation sync not yet implemented');
    } catch (error) {
      console.error('❌ Error syncing revoked status:', error);
    }
  }
}
